import store from "@invesco/shared/store/index";
import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import analytics from "@react-native-firebase/analytics";
import * as Application from "expo-application";
import Constants from "expo-constants";
import { Platform } from "react-native";

const isNative = Platform.OS === "android" || Platform.OS === "ios";

// Event name is now a string to allow maximum flexibility as per team lead's feedback

interface EventParams {
  click_text?: string;
  click_category?: string;
  click_header?: string;
  custom_current_page?: string;
  custom_previous_page?: string;
  user_status?: string;
  userID?: string;
  custom_index?: number | string;
  search_term?: string;
  no_of_results?: number;
  no_of_funds?: number | string;
  investment_period?: string;
  categories_selected?: string;
  plan_option_selected?: string;
  investment_type?: string;
  investment_option?: string;
  investment_plan?: string;
  folio?: string;
  custom_nav?: string | number;
  custom_cagr?: string | number;
  item_name?: string;
  frequency?: string;
  affiliation?: string;
  item_list_id?: string;
  item_list_name?: string;
  item_category2?: string;
  [key: string]: any;
}

function getCommonParams() {
  const state = (store?.getState?.() as any) || ({} as any);
  const pan = state?.user?.pan || "-";
  const token = state?.user?.token;
  const isLoggedIn = state?.user?.isLoggedIn;
  const user_status = isLoggedIn ? "Logged In" : "Guest";

  // Hash PAN to create userID for privacy (hex-encoded SHA-256, same format
  // as the previous CryptoJS.SHA256(...).toString() so downstream analytics
  // identifiers remain stable).
  const userID = pan !== "-" ? bytesToHex(sha256(utf8ToBytes(pan))) : "-";

  const app_version =
    Constants.expoConfig?.version ?? Application.nativeApplicationVersion ?? "0.0.0";
  const platform = Platform.OS;

  return { userID, user_status, app_version, platform };
}

export const Analytics = {
  /**
   * Identifies the user in Firebase.
   * Should be called after successful login or when app state updates with user info.
   */
  async identifyUser(userId: string) {
    if (!isNative || !userId || userId === "-" || userId === "unknown") return;
    try {
      await analytics().setUserId(String(userId));
      await analytics().setUserProperties({ pan: userId });
      // console.log("[Analytics] User ID & Properties set:", userId);
    } catch (error) {
      console.error("[Analytics] Error identifying user:", error);
    }
  },

  /**
   * Generic event logger that merges common parameters with event-specific data.
   */
  async logEvent(name: string, params?: EventParams) {
    if (!isNative) return;
    try {
      const common = getCommonParams();

      // Ensure user is identified if userID is available
      if (common.userID && common.userID !== "-") {
        await this.identifyUser(common.userID);
      }

      const eventData = {
        ...common,
        ...params,
      };

      // Flatten ecommerce object if it exists (GA4 Mobile expectation: items should be top-level)
      let finalParams: any = { ...eventData };
      if (finalParams.ecommerce) {
        const { ecommerce, ...rest } = finalParams;
        finalParams = {
          ...rest,
          ...ecommerce,
        };
      }

      // Globally remove PAN from parameters to ensure privacy
      delete finalParams.pan;

      await analytics().logEvent(name, finalParams);
      // console.log(`[Analytics] [Event] ${name}:`, JSON.stringify(finalParams, null, 2));
    } catch (error) {
      console.error(`[Analytics] Error logging event ${name}:`, error);
    }
  },

  async logScreenView(screenName: string, screenClass?: string) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      if (common.userID && common.userID !== "-") {
        await this.identifyUser(common.userID);
      }

      const params = {
        screen_class: screenClass || screenName,
        ...common,
      };

      await analytics().logScreenView({
        screen_name: screenName,
        screen_class: screenClass || screenName,
      });
      // console.log(`[Analytics] [Screen] ${screenName}:`, {
      //   screen_class: screenClass || screenName,
      //   ...common,
      // });
      // console.log(`[Analytics] [Screen] ${screenName}:`, params);
    } catch (error) {
      console.error("[Analytics] Error logging screen view:", error);
    }
  },

  async logPurchase(params: {
    transactionId: string;
    value: number;
    currency?: string;
    payment_type?: string;
    custom_current_page?: string;
    custom_previous_page?: string;
    investment_type?: string;
    folio?: string;
    frequency?: string;
    items: Array<{
      item_id: string;
      item_name: string;
      affiliation?: string;
      index?: number;
      investment_option?: string;
      investment_plan?: string;
      item_category?: string;
      item_category2?: string;
      item_list_id?: string;
      item_list_name?: string;
      price: number;
      quantity?: number;
    }>;
  }) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      if (common.userID && common.userID !== "-") {
        await this.identifyUser(common.userID);
      }

      const purchaseParams = {
        transaction_id: params.transactionId,
        value: params.value,
        currency: params.currency || "INR",
        payment_type: params.payment_type,
        custom_current_page: params.custom_current_page || "thankyou page",
        custom_previous_page: params.custom_previous_page || "add payment info",
        investment_type: params.investment_type,
        folio: params.folio,
        frequency: params.frequency,
        items: params.items,
        ...common,
      };

      // Globally remove PAN from parameters to ensure privacy
      delete (purchaseParams as any).pan;

      await analytics().logEvent("purchase", purchaseParams);
      // console.log("[Analytics] [Purchase]:", JSON.stringify(purchaseParams, null, 2));
    } catch (error) {
      console.error("[Analytics] Error logging purchase:", error);
    }
  },

  async logSwitchSuccess(params: {
    click_text: string;
    click_category: string;
    fund_out_from: string;
    fund_in_to: string;
    custom_amount: number | string;
    custom_current_page?: string;
    custom_previous_page?: string;
    folio?: string;
  }) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      const eventData = {
        ...params,
        custom_current_page: params.custom_current_page || "thankyou page",
        custom_previous_page: params.custom_previous_page || "funds switch",
        ...common,
      };
      await analytics().logEvent("switch_successful", eventData);
      // console.log(
      //   "[Analytics] Switch successful logged:",
      //   JSON.stringify(eventData, null, 2),
      // );
    } catch (error) {
      console.error("[Analytics] Error logging switch success:", error);
    }
  },

  async logStpSuccess(params: {
    click_text: string;
    click_category: string;
    fund_out_from: string;
    fund_in_to: string;
    custom_amount: number | string;
    folio?: string;
    investment_plan?: string;
    investment_option?: string;
    stp_type?: string;
    frequency?: string;
    custom_current_page?: string;
    custom_previous_page?: string;
  }) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      const eventData = {
        ...params,
        custom_current_page: params.custom_current_page || "thankyou page",
        custom_previous_page: params.custom_previous_page || "Start STP",
        ...common,
      };
      await analytics().logEvent("stp_successful", eventData);
      // console.log(
      //   "[Analytics] STP successful logged:",
      //   JSON.stringify(eventData, null, 2),
      // );
    } catch (error) {
      console.error("[Analytics] Error logging stp success:", error);
    }
  },

  async logSwpSuccess(params: {
    click_text: string;
    click_category: string;
    custom_amount: number | string;
    folio?: string;
    investment_plan?: string;
    investment_option?: string;
    swp_type?: string;
    frequency?: string;
    custom_current_page?: string;
    custom_previous_page?: string;
    bank_name?: string;
    items?: any[];
  }) {
    if (!isNative) return;
    try {
      const common = getCommonParams();
      const eventData: any = {
        ...params,
        custom_current_page: params.custom_current_page || "thankyou page",
        ...common,
      };

      if (params.items) {
        eventData.currency = "INR";
        eventData.transaction_id = params.folio || "T_N/A";
        eventData.bank_name = params.bank_name;
        eventData.items = params.items;
      }

      await analytics().logEvent("swp_successful", eventData);
      // console.log(
      //   "[Analytics] SWP successful logged:",
      //   JSON.stringify(eventData, null, 2),
      // );
    } catch (error) {
      console.error("[Analytics] Error logging swp success:", error);
    }
  },
};
